// src/components/ClientManagement.js
import React, { useState, useEffect } from 'react';
import {
  Box,
  VStack,
  HStack,
  Button,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalCloseButton,
  ModalFooter,
  FormControl,
  FormLabel,
  Input,
  Select,
  Textarea,
  Switch,
  Checkbox,
  NumberInput,
  NumberInputField,
  IconButton,
  Divider,
  useToast,
  useDisclosure,
  Text,
  Badge,
  Stat,
  StatLabel,
  StatNumber,
  Spinner,
  Heading,
  Accordion,
  AccordionItem,
  AccordionButton,
  AccordionPanel,
  AccordionIcon,
  Alert,
  AlertIcon,
  Progress,
  List,
  ListItem,
  ListIcon,
  CloseButton,
  Stack,
  Card,
  CardBody,
  Tooltip,
  Tabs,
  TabList,
  TabPanels,
  Tab,
  TabPanel,
  Center,
  ButtonGroup,
} from '@chakra-ui/react';
import { AddIcon, DeleteIcon, EditIcon, ChevronLeftIcon, AttachmentIcon, WarningIcon, CheckCircleIcon, InfoIcon, ViewIcon } from '@chakra-ui/icons';
import ClientLongevityView from './ClientLongevityView';
import supabase from '../supabaseClient';
import { calculateAgeFromBirthdate, getClientDisplayAge } from '../utils/clientAge';
import { formatNameLastCommaFirst, compareNamesByFamilyName } from '../utils/clientNameDisplay';
import {
  formatAttendanceStatusLabel,
  getAttendanceStatusColorScheme,
  isCancelledOrLateCancellationStatus,
  formatMakeupBadgeDate,
} from '../utils/attendanceDisplay';
import {
  getLocalDateString,
  isPricingActiveOnDate,
  parseLocalDateString,
} from '../utils/dateUtils';
import { isPermanentClientSession } from '../utils/serviceTypeUtils';

// Document Upload Component
const DocumentUploadSection = ({ clientId, isNewClient = false }) => {
  const [documents, setDocuments] = useState([]);
  const [uploadingFiles, setUploadingFiles] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [uploadQueue, setUploadQueue] = useState([]);
  const toast = useToast();

  useEffect(() => {
    if (clientId && !isNewClient) {
      fetchClientDocuments();
    }
  }, [clientId, isNewClient]);

  const fetchClientDocuments = async () => {
    try {
      setIsLoading(true);
      const { data, error } = await supabase
        .from('client_documents')
        .select('*')
        .eq('client_id', clientId)
        .order('uploaded_at', { ascending: false });

      if (error) throw error;
      setDocuments(data || []);
    } catch (error) {
      toast({
        title: 'Error fetching documents',
        description: error.message,
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleFileSelect = (event, category, type) => {
    const files = Array.from(event.target.files);
    if (files.length === 0) return;

    const newUploads = files.map(file => ({
      id: Date.now() + Math.random(),
      file,
      category,
      type,
      title: file.name.split('.')[0],
      description: '',
      status: 'pending', // pending, uploading, completed, error
      progress: 0,
      error: null
    }));

    setUploadQueue(prev => [...prev, ...newUploads]);
  };

  const updateUploadItem = (id, updates) => {
    setUploadQueue(prev => 
      prev.map(item => 
        item.id === id ? { ...item, ...updates } : item
      )
    );
  };

  const removeUploadItem = (id) => {
    setUploadQueue(prev => prev.filter(item => item.id !== id));
  };

  const uploadFile = async (uploadItem) => {
    try {
      updateUploadItem(uploadItem.id, { status: 'uploading', progress: 0 });

      // Create file path
      const fileExt = uploadItem.file.name.split('.').pop();
      const fileName = `${Date.now()}-${Math.random().toString(36).substring(2)}.${fileExt}`;
      const filePath = `${clientId}/${uploadItem.category.replace(' ', '_').toLowerCase()}/${fileName}`;

      // Upload to Supabase Storage
      const { data: uploadData, error: uploadError } = await supabase.storage
        .from('client-documents')
        .upload(filePath, uploadItem.file, {
          onUploadProgress: (progress) => {
            const percentage = (progress.loaded / progress.total) * 100;
            updateUploadItem(uploadItem.id, { progress: percentage });
          }
        });

      if (uploadError) throw uploadError;

      // Save document metadata to database
      const { error: dbError } = await supabase
        .from('client_documents')
        .insert({
          client_id: clientId,
          document_category: uploadItem.category,
          document_type: uploadItem.type,
          title: uploadItem.title || uploadItem.file.name.split('.')[0],
          description: uploadItem.description,
          file_name: uploadItem.file.name,
          file_path: filePath,
          file_size: uploadItem.file.size,
          mime_type: uploadItem.file.type
        });

      if (dbError) throw dbError;

      updateUploadItem(uploadItem.id, { 
        status: 'completed', 
        progress: 100 
      });

      // Refresh documents list
      if (!isNewClient) {
        fetchClientDocuments();
      }

      toast({
        title: 'Document uploaded successfully',
        status: 'success',
        duration: 3000,
        isClosable: true,
      });

    } catch (error) {
      updateUploadItem(uploadItem.id, { 
        status: 'error', 
        error: error.message 
      });
      
      toast({
        title: 'Upload failed',
        description: error.message,
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    }
  };

  const uploadAllPending = async () => {
    const pendingUploads = uploadQueue.filter(item => item.status === 'pending');
    
    for (const upload of pendingUploads) {
      await uploadFile(upload);
    }
  };

  const documentCategories = [
    {
      category: 'Initial Documents',
      types: ['DevPed Report', 'Doctor Referral', 'Previous Progress Report', 'Other'],
      description: 'Documents provided during initial consultation'
    },
    {
      category: 'Evaluations', 
      types: ['Initial Evaluation', 'Progress Report', 'Other'],
      description: 'Assessment and evaluation reports'
    },
    {
      category: 'Medical Records',
      types: ['Medical Document', 'Insurance Document', 'Other'],
      description: 'Medical history and insurance documents'
    },
    {
      category: 'Administrative',
      types: ['Insurance Document', 'Other'],
      description: 'Administrative and billing documents'
    }
  ];

  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'completed': return 'green';
      case 'uploading': return 'blue';
      case 'error': return 'red';
      default: return 'gray';
    }
  };

  const downloadDocument = async (doc) => {
    try {
      // Get signed URL for download
      const { data, error } = await supabase.storage
        .from('client-documents')
        .createSignedUrl(doc.file_path, 60); // URL valid for 60 seconds

      if (error) throw error;

      // Create a temporary link and trigger download
      const link = document.createElement('a');
      link.href = data.signedUrl;
      link.download = doc.file_name;
      link.target = '_blank';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      toast({
        title: 'Download started',
        description: `Downloading ${doc.file_name}`,
        status: 'success',
        duration: 3000,
        isClosable: true,
      });

    } catch (error) {
      toast({
        title: 'Download failed',
        description: error.message,
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    }
  };

  if (isNewClient) {
    return (
      <Alert status="info">
        <AlertIcon />
        Document upload will be available after saving the client.
      </Alert>
    );
  }

  return (
    <Box>
      <VStack spacing={4} align="stretch">
        {/* Upload Queue */}
        {uploadQueue.length > 0 && (
          <Card>
            <CardBody>
              <HStack justify="space-between" mb={3}>
                <Heading size="sm">Upload Queue</Heading>
                <Button 
                  size="sm" 
                  colorScheme="blue"
                  onClick={uploadAllPending}
                  isDisabled={!uploadQueue.some(item => item.status === 'pending')}
                >
                  Upload All
                </Button>
              </HStack>
              <VStack spacing={2} align="stretch">
                {uploadQueue.map(item => (
                  <Box key={item.id} p={3} borderWidth="1px" borderRadius="md">
                    <HStack justify="space-between" mb={2}>
                      <VStack align="start" spacing={0} flex={1}>
                        <Text fontSize="sm" fontWeight="medium">{item.file.name}</Text>
                        <Text fontSize="xs" color="gray.600">
                          {item.category} - {item.type} ({formatFileSize(item.file.size)})
                        </Text>
                      </VStack>
                      <HStack>
                        <Badge colorScheme={getStatusColor(item.status)}>
                          {item.status}
                        </Badge>
                        <CloseButton 
                          size="sm" 
                          onClick={() => removeUploadItem(item.id)}
                          isDisabled={item.status === 'uploading'}
                        />
                      </HStack>
                    </HStack>
                    
                    {item.status === 'uploading' && (
                      <Progress value={item.progress} size="sm" colorScheme="blue" />
                    )}
                    
                    {item.status === 'error' && (
                      <Text fontSize="xs" color="red.500">{item.error}</Text>
                    )}
                    
                    {item.status === 'pending' && (
                      <VStack spacing={2} align="stretch">
                        <Input
                          placeholder="Document title"
                          value={item.title}
                          onChange={(e) => updateUploadItem(item.id, { title: e.target.value })}
                          size="sm"
                        />
                        <Input
                          placeholder="Description (optional)"
                          value={item.description}
                          onChange={(e) => updateUploadItem(item.id, { description: e.target.value })}
                          size="sm"
                        />
                      </VStack>
                    )}
                  </Box>
                ))}
              </VStack>
            </CardBody>
          </Card>
        )}

        {/* Upload Sections */}
        <Accordion allowMultiple>
          {documentCategories.map((categoryInfo, index) => (
            <AccordionItem key={index}>
              <AccordionButton>
                <Box flex="1" textAlign="left">
                  <Text fontWeight="medium">{categoryInfo.category}</Text>
                  <Text fontSize="sm" color="gray.600">{categoryInfo.description}</Text>
                </Box>
                <AccordionIcon />
              </AccordionButton>
              <AccordionPanel pb={4}>
                <VStack spacing={3} align="stretch">
                  {categoryInfo.types.map(type => (
                    <Box key={type}>
                      <FormLabel fontSize="sm">{type}</FormLabel>
                      <Input
                        type="file"
                        accept=".pdf,.doc,.docx,.jpg,.jpeg,.png"
                        multiple
                        onChange={(e) => handleFileSelect(e, categoryInfo.category, type)}
                        size="sm"
                      />
                    </Box>
                  ))}
                </VStack>
              </AccordionPanel>
            </AccordionItem>
          ))}
        </Accordion>

        {/* Existing Documents */}
        {documents.length > 0 && (
          <Card>
            <CardBody>
              <Heading size="sm" mb={3}>Uploaded Documents</Heading>
              <VStack spacing={2} align="stretch">
                {documents.map(doc => (
                  <HStack key={doc.id} p={2} borderWidth="1px" borderRadius="md">
                    <AttachmentIcon color="blue.500" />
                    <VStack align="start" spacing={0} flex={1}>
                      <Text fontSize="sm" fontWeight="medium">{doc.title}</Text>
                      <Text fontSize="xs" color="gray.600">
                        {doc.document_category} - {doc.document_type}
                      </Text>
                      <Text fontSize="xs" color="gray.500">
                        {formatFileSize(doc.file_size)} • {new Date(doc.uploaded_at).toLocaleDateString()}
                      </Text>
                    </VStack>
                    <Button 
                      size="xs" 
                      variant="outline"
                      onClick={() => downloadDocument(doc)}
                    >
                      Download
                    </Button>
                  </HStack>
                ))}
              </VStack>
            </CardBody>
          </Card>
        )}
      </VStack>
    </Box>
  );
};

const ClientDetailModal = ({ isOpen, onClose, client }) => {
  const [documents, setDocuments] = useState([]);
  const [isLoadingDocs, setIsLoadingDocs] = useState(false);
  const [activeDocTab, setActiveDocTab] = useState(0);
  
  // Pagination states
  const [currentPage, setCurrentPage] = useState(1);
  const [totalDocuments, setTotalDocuments] = useState(0);
  const [documentsPerPage] = useState(10);
  
  // Tab-specific pagination
  const [tabPagination, setTabPagination] = useState({
    initial: { currentPage: 1, total: 0 },
    recent: { currentPage: 1, total: 0 },
    evaluations: { currentPage: 1, total: 0 },
    all: { currentPage: 1, total: 0 }
  });
  
  const toast = useToast();

  useEffect(() => {
    if (client && isOpen) {
      fetchCategoryCounts();
      // Then load the Initial Documents tab
      handleTabChange(0);
    }
  }, [client, isOpen]);

  // Reset pagination when tab changes
  useEffect(() => {
    setCurrentPage(1);
  }, [activeDocTab]);

  const fetchClientDocuments = async (page = 1, category = null) => {
    try {
      setIsLoadingDocs(true);
      
      // First get the count to validate pagination
      let countQuery = supabase
        .from('client_documents')
        .select('*', { count: 'exact', head: true })
        .eq('client_id', client.id);

      if (category) {
        countQuery = countQuery.eq('document_category', category);
      }

      const { count, error: countError } = await countQuery;
      
      if (countError) throw countError;

      const totalCount = count || 0;
      setTotalDocuments(totalCount);

      // If no documents, return early
      if (totalCount === 0) {
        setDocuments([]);
        setCurrentPage(1);
        await fetchCategoryCounts();
        return;
      }

      // Validate page number
      const maxPage = Math.ceil(totalCount / documentsPerPage);
      const validPage = Math.min(Math.max(1, page), maxPage);
      
      // Calculate pagination range
      const from = (validPage - 1) * documentsPerPage;
      const to = Math.min(from + documentsPerPage - 1, totalCount - 1);

      // Only apply range if we have documents
      let dataQuery = supabase
        .from('client_documents')
        .select('*')
        .eq('client_id', client.id)
        .order('uploaded_at', { ascending: false });

      if (category) {
        dataQuery = dataQuery.eq('document_category', category);
      }

      dataQuery = dataQuery.range(from, to);

      const { data, error } = await dataQuery;
  
      if (error) throw error;
      
      setDocuments(data || []);
      setCurrentPage(validPage);

      // Fetch counts for badges (only once)
      if (page === 1) {
        await fetchCategoryCounts();
      }
      
    } catch (error) {
      console.error('Fetch error:', error);
      toast({
        title: 'Error fetching documents',
        description: error.message,
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setIsLoadingDocs(false);
    }
  };

  const fetchCategoryCounts = async () => {
    try {
      // Get counts for each category
      const categories = ['Initial Documents', 'Session Notes', ['Evaluations', 'Progress Reports']];
      const counts = {};

      // Initial Documents count
      const { count: initialCount } = await supabase
        .from('client_documents')
        .select('*', { count: 'exact', head: true })
        .eq('client_id', client.id)
        .eq('document_category', 'Initial Documents');

      // Recent Session Notes count
      const { count: notesCount } = await supabase
        .from('client_documents')
        .select('*', { count: 'exact', head: true })
        .eq('client_id', client.id)
        .eq('document_category', 'Session Notes');

      // Evaluations and Progress Reports count
      const { count: evaluationsCount } = await supabase
        .from('client_documents')
        .select('*', { count: 'exact', head: true })
        .eq('client_id', client.id)
        .in('document_category', ['Evaluations', 'Progress Reports']);

      // Total count
      const { count: totalCount } = await supabase
        .from('client_documents')
        .select('*', { count: 'exact', head: true })
        .eq('client_id', client.id);

      setTabPagination({
        initial: { currentPage: 1, total: initialCount || 0 },
        recent: { currentPage: 1, total: notesCount || 0 },
        evaluations: { currentPage: 1, total: evaluationsCount || 0 },
        all: { currentPage: 1, total: totalCount || 0 }
      });

    } catch (error) {
      console.error('Error fetching category counts:', error);
    }
  };

  const handleTabChange = async (tabIndex) => {
    setActiveDocTab(tabIndex);
    
    // Don't fetch documents for upload tab
    if (tabIndex === 3) {
      return;
    }
    
    setIsLoadingDocs(true);
    
    try {
      // Get count first
      let countQuery = supabase
        .from('client_documents')
        .select('*', { count: 'exact', head: true })
        .eq('client_id', client.id);

      let category = null;
      switch (tabIndex) {
        case 0: // Initial Documents
          countQuery = countQuery.eq('document_category', 'Initial Documents');
          category = 'Initial Documents';
          break;
        case 1: // Recent Notes
          countQuery = countQuery.eq('document_category', 'Session Notes');
          category = 'Session Notes';
          break;
        case 2: // Evaluations
          countQuery = countQuery.in('document_category', ['Evaluations', 'Progress Reports']);
          break;
        case 4: // All Documents - no filter
          break;
        default:
          break;
      }

      const { count, error: countError } = await countQuery;
      if (countError) throw countError;

      const totalCount = count || 0;
      setTotalDocuments(totalCount);

      // If no documents, return early
      if (totalCount === 0) {
        setDocuments([]);
        setCurrentPage(1);
        setIsLoadingDocs(false);
        return;
      }

      // Fetch first page of documents
      let dataQuery = supabase
        .from('client_documents')
        .select('*')
        .eq('client_id', client.id)
        .order('uploaded_at', { ascending: false });

      switch (tabIndex) {
        case 0: // Initial Documents
          dataQuery = dataQuery.eq('document_category', 'Initial Documents');
          break;
        case 1: // Recent Notes
          dataQuery = dataQuery.eq('document_category', 'Session Notes');
          break;
        case 2: // Evaluations
          dataQuery = dataQuery.in('document_category', ['Evaluations', 'Progress Reports']);
          break;
        case 4: // All Documents - no additional filter
          break;
        default:
          break;
      }

      // Apply pagination - calculate safe range
      const from = 0; // Always start from first page
      const to = Math.min(documentsPerPage - 1, totalCount - 1);
      
      dataQuery = dataQuery.range(from, to);
      const { data, error } = await dataQuery;

      if (error) throw error;
      
      setDocuments(data || []);
      setCurrentPage(1);

    } catch (error) {
      console.error('Tab change error:', error);
      toast({
        title: 'Error fetching documents',
        description: error.message,
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setIsLoadingDocs(false);
    }
  };

  const handlePageChange = async (newPage) => {
    setIsLoadingDocs(true);
    
    try {
      // Get current count first to validate page
      let countQuery = supabase
        .from('client_documents')
        .select('*', { count: 'exact', head: true })
        .eq('client_id', client.id);

      // Apply same filters as current tab
      switch (activeDocTab) {
        case 0: // Initial Documents
          countQuery = countQuery.eq('document_category', 'Initial Documents');
          break;
        case 1: // Recent Notes
          countQuery = countQuery.eq('document_category', 'Session Notes');
          break;
        case 2: // Evaluations
          countQuery = countQuery.in('document_category', ['Evaluations', 'Progress Reports']);
          break;
        case 4: // All Documents - no additional filter
          break;
        default:
          break;
      }

      const { count, error: countError } = await countQuery;
      if (countError) throw countError;

      const totalCount = count || 0;
      setTotalDocuments(totalCount);

      // Validate page
      const maxPage = Math.ceil(totalCount / documentsPerPage);
      const validPage = Math.min(Math.max(1, newPage), maxPage);

      if (totalCount === 0) {
        setDocuments([]);
        setCurrentPage(1);
        setIsLoadingDocs(false);
        return;
      }

      // Fetch documents for the validated page
      let dataQuery = supabase
        .from('client_documents')
        .select('*')
        .eq('client_id', client.id)
        .order('uploaded_at', { ascending: false });

      // Apply same filters as current tab
      switch (activeDocTab) {
        case 0: // Initial Documents
          dataQuery = dataQuery.eq('document_category', 'Initial Documents');
          break;
        case 1: // Recent Notes
          dataQuery = dataQuery.eq('document_category', 'Session Notes');
          break;
        case 2: // Evaluations
          dataQuery = dataQuery.in('document_category', ['Evaluations', 'Progress Reports']);
          break;
        case 4: // All Documents - no additional filter
          break;
        default:
          break;
      }

      const from = (validPage - 1) * documentsPerPage;
      const to = Math.min(from + documentsPerPage - 1, totalCount - 1);
      
      dataQuery = dataQuery.range(from, to);
      const { data, error } = await dataQuery;

      if (error) throw error;
      
      setDocuments(data || []);
      setCurrentPage(validPage);

    } catch (error) {
      console.error('Page change error:', error);
      toast({
        title: 'Error fetching documents',
        description: error.message,
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setIsLoadingDocs(false);
    }
  };

  const downloadDocument = async (doc) => {
    try {
      const { data, error } = await supabase.storage
        .from('client-documents')
        .createSignedUrl(doc.file_path, 60);

      if (error) throw error;

      const link = document.createElement('a');
      link.href = data.signedUrl;
      link.download = doc.file_name;
      link.target = '_blank';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      toast({
        title: 'Download started',
        description: `Downloading ${doc.file_name}`,
        status: 'success',
        duration: 3000,
        isClosable: true,
      });
    } catch (error) {
      toast({
        title: 'Download failed',
        description: error.message,
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    }
  };

  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const DocumentList = ({ docs, showCategory = false }) => {
    if (isLoadingDocs) {
      return (
        <Center py={8}>
          <VStack spacing={3}>
            <Spinner size="lg" />
            <Text color="gray.500">Loading documents...</Text>
          </VStack>
        </Center>
      );
    }

    if (docs.length === 0) {
      return (
        <Text color="gray.500" fontStyle="italic" textAlign="center" py={4}>
          No documents found
        </Text>
      );
    }

    return (
      <VStack spacing={4} align="stretch">
        <VStack spacing={2} align="stretch">
          {docs.map(doc => (
            <HStack key={doc.id} p={3} borderWidth="1px" borderRadius="md" bg="gray.50">
              <AttachmentIcon color="blue.500" />
              <VStack align="start" spacing={0} flex={1}>
                <Text fontSize="sm" fontWeight="medium">{doc.title}</Text>
                {showCategory && (
                  <Text fontSize="xs" color="blue.600">
                    {doc.document_category} - {doc.document_type}
                  </Text>
                )}
                <Text fontSize="xs" color="gray.600">
                  {doc.document_type} • {formatFileSize(doc.file_size)}
                </Text>
                <Text fontSize="xs" color="gray.500">
                  {new Date(doc.uploaded_at).toLocaleDateString('en-US', {
                    year: 'numeric',
                    month: 'short',
                    day: 'numeric',
                    hour: '2-digit',
                    minute: '2-digit'
                  })}
                </Text>
                {doc.description && (
                  <Text fontSize="xs" color="gray.600" fontStyle="italic">
                    {doc.description}
                  </Text>
                )}
              </VStack>
              <Button 
                size="xs" 
                variant="outline"
                colorScheme="blue"
                onClick={() => downloadDocument(doc)}
              >
                Download
              </Button>
            </HStack>
          ))}
        </VStack>

        {/* Pagination */}
        {totalDocuments > documentsPerPage && (
          <PaginationControls />
        )}
      </VStack>
    );
  };

  const PaginationControls = () => {
    const totalPages = Math.ceil(totalDocuments / documentsPerPage);
    const startDoc = (currentPage - 1) * documentsPerPage + 1;
    const endDoc = Math.min(currentPage * documentsPerPage, totalDocuments);

    return (
      <VStack spacing={3}>
        <Text fontSize="sm" color="gray.600" textAlign="center">
          Showing {startDoc}-{endDoc} of {totalDocuments} documents
        </Text>
        
        <HStack spacing={2} justify="center">
          <Button
            size="sm"
            onClick={() => handlePageChange(currentPage - 1)}
            isDisabled={currentPage === 1 || isLoadingDocs}
            variant="outline"
          >
            Previous
          </Button>
          
          {/* Page numbers */}
          {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
            let pageNum;
            if (totalPages <= 5) {
              pageNum = i + 1;
            } else if (currentPage <= 3) {
              pageNum = i + 1;
            } else if (currentPage >= totalPages - 2) {
              pageNum = totalPages - 4 + i;
            } else {
              pageNum = currentPage - 2 + i;
            }

            return (
              <Button
                key={pageNum}
                size="sm"
                onClick={() => handlePageChange(pageNum)}
                isDisabled={isLoadingDocs}
                colorScheme={currentPage === pageNum ? "blue" : "gray"}
                variant={currentPage === pageNum ? "solid" : "outline"}
              >
                {pageNum}
              </Button>
            );
          })}
          
          <Button
            size="sm"
            onClick={() => handlePageChange(currentPage + 1)}
            isDisabled={currentPage === totalPages || isLoadingDocs}
            variant="outline"
          >
            Next
          </Button>
        </HStack>
      </VStack>
    );
  };

  if (!client) return null;

  const displayAge = getClientDisplayAge(client);

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="4xl">
      <ModalOverlay />
      <ModalContent maxH="90vh" overflowY="auto">
        <ModalHeader>
          <VStack align="start" spacing={1}>
            <Text>{formatNameLastCommaFirst(client.name)}</Text>
            {client.nickname && (
              <Text fontSize="md" color="gray.600" fontWeight="normal">
                "{client.nickname}"
              </Text>
            )}
          </VStack>
        </ModalHeader>
        <ModalCloseButton />
        <ModalBody pb={6}>
          <VStack spacing={6} align="stretch">
            {/* Basic Information */}
            <Card>
              <CardBody>
                <Heading size="sm" mb={3}>Basic Information</Heading>
                <VStack spacing={2} align="stretch">
                  <HStack>
                    <Text fontWeight="medium" minW="120px">Age:</Text>
                    <Text>{displayAge !== null ? displayAge : 'Not specified'}</Text>
                  </HStack>
                  <HStack>
                    <Text fontWeight="medium" minW="120px">Gender:</Text>
                    <Text>{client.gender || 'Not specified'}</Text>
                  </HStack>
                  <HStack>
                    <Text fontWeight="medium" minW="120px">Diagnosis:</Text>
                    <Text>{client.diagnosis || 'Not specified'}</Text>
                  </HStack>
                  <HStack>
                    <Text fontWeight="medium" minW="120px">PWD Status:</Text>
                    <Text>{client.pwd ? `PWD - ${client.pwd_number || 'No ID'}` : 'Non-PWD'}</Text>
                  </HStack>
                </VStack>
              </CardBody>
            </Card>

            {/* Guardian Information */}
            <Card>
              <CardBody>
                <Heading size="sm" mb={3}>Guardian Information</Heading>
                <VStack spacing={2} align="stretch">
                  <HStack>
                    <Text fontWeight="medium" minW="120px">Name:</Text>
                    <Text>{client.guardian_name || 'Not specified'}</Text>
                  </HStack>
                  <HStack>
                    <Text fontWeight="medium" minW="120px">Contact:</Text>
                    <Text>{client.guardian_contact || 'Not specified'}</Text>
                  </HStack>
                  <HStack>
                    <Text fontWeight="medium" minW="120px">Email:</Text>
                    <Text>{client.guardian_email || 'Not specified'}</Text>
                  </HStack>
                </VStack>
              </CardBody>
            </Card>

            {/* Services */}
            <Card>
              <CardBody>
                <Heading size="sm" mb={3}>Services & Schedule</Heading>
                <VStack spacing={3} align="stretch">
                  {client.services && client.services.length > 0 ? (
                    client.services.map((service, index) => (
                      <Box key={index} p={3} borderWidth="1px" borderRadius="md">
                        <VStack align="start" spacing={1}>
                          <Text fontWeight="medium">
                            {service.service_type} - {service.frequency}
                          </Text>
                          {service.therapists && service.therapists.length > 0 ? (
                            <Text fontSize="sm" color="gray.600">
                              Therapist/s: {service.therapists.join(', ')}
                            </Text>
                          ) : (
                            <Text fontSize="sm" color="gray.400" fontStyle="italic">
                              No therapists assigned
                            </Text>
                          )}
                          <Text fontSize="sm" color="gray.500">
                            Schedule: {service.schedule || 'Not specified'}
                          </Text>
                        </VStack>
                      </Box>
                    ))
                  ) : (
                    <Text color="gray.500" fontStyle="italic">No services assigned</Text>
                  )}
                </VStack>
              </CardBody>
            </Card>

            {/* Notes */}
            {client.notes && (
              <Card>
                <CardBody>
                  <Heading size="sm" mb={3}>Notes</Heading>
                  <Text>{client.notes}</Text>
                </CardBody>
              </Card>
            )}

            {/* Document Management with Tabs and Pagination */}
            <Card>
              <CardBody>
                <HStack justify="space-between" mb={3}>
                  <Heading size="sm">Documents</Heading>
                  <Badge colorScheme="blue">{tabPagination.all.total} total</Badge>
                </HStack>
                
                <Tabs index={activeDocTab} onChange={handleTabChange}>
                  <TabList>
                    <Tab>
                      Initial Documents
                      {tabPagination.initial.total > 0 && (
                        <Badge ml={2} colorScheme="red" variant="solid">
                          {tabPagination.initial.total}
                        </Badge>
                      )}
                    </Tab>
                    <Tab>
                      Recent Notes
                      {tabPagination.recent.total > 0 && (
                        <Badge ml={2} colorScheme="green" variant="solid">
                          {tabPagination.recent.total}
                        </Badge>
                      )}
                    </Tab>
                    <Tab>
                      Evaluations
                      {tabPagination.evaluations.total > 0 && (
                        <Badge ml={2} colorScheme="purple" variant="solid">
                          {tabPagination.evaluations.total}
                        </Badge>
                      )}
                    </Tab>
                    <Tab>
                      Upload Documents
                      <Badge ml={2} colorScheme="blue" variant="solid">
                        +
                      </Badge>
                    </Tab>
                    <Tab>
                      All Documents
                      <Badge ml={2} colorScheme="blue" variant="solid">
                        {tabPagination.all.total}
                      </Badge>
                    </Tab>
                  </TabList>

                  <TabPanels>
                    {/* Initial Documents Tab */}
                    <TabPanel px={0} py={4}>
                      <VStack align="stretch" spacing={3}>
                        <Alert status="info" size="sm">
                          <AlertIcon />
                          <Text fontSize="sm">
                            These are the documents submitted during initial consultation.
                          </Text>
                        </Alert>
                        <DocumentList docs={documents} />
                      </VStack>
                    </TabPanel>

                    {/* Recent Session Notes Tab */}
                    <TabPanel px={0} py={4}>
                      <VStack align="stretch" spacing={3}>
                        <Alert status="info" size="sm">
                          <AlertIcon />
                          <Text fontSize="sm">
                            Session notes ordered by most recent.
                          </Text>
                        </Alert>
                        <DocumentList docs={documents} />
                      </VStack>
                    </TabPanel>

                    {/* Evaluations Tab */}
                    <TabPanel px={0} py={4}>
                      <VStack align="stretch" spacing={3}>
                        <Alert status="info" size="sm">
                          <AlertIcon />
                          <Text fontSize="sm">
                            Evaluation reports and progress assessments.
                          </Text>
                        </Alert>
                        <DocumentList docs={documents} />
                      </VStack>
                    </TabPanel>

                    {/* Upload Documents Tab */}
                    <TabPanel px={0} py={4}>
                      <VStack align="stretch" spacing={3}>
                        <Alert status="info" size="sm">
                          <AlertIcon />
                          <Text fontSize="sm">
                            Upload additional documents for this client.
                          </Text>
                        </Alert>
                        <DocumentUploadSection 
                          clientId={client.id} 
                          isNewClient={false}
                        />
                      </VStack>
                    </TabPanel>

                    {/* All Documents Tab */}
                    <TabPanel px={0} py={4}>
                      <VStack align="stretch" spacing={3}>
                        <Alert status="info" size="sm">
                          <AlertIcon />
                          <Text fontSize="sm">
                            All documents for this client, grouped by category.
                          </Text>
                        </Alert>
                        <DocumentList docs={documents} showCategory={true} />
                      </VStack>
                    </TabPanel>
                  </TabPanels>
                </Tabs>
              </CardBody>
            </Card>
          </VStack>
        </ModalBody>
      </ModalContent>
    </Modal>
  );
};

// Updated ServiceRow component in ClientForm
const ServiceRow = ({ service, onDelete, onUpdate }) => {
  return (
    <VStack spacing={3} align="stretch" width="100%">
    <HStack spacing={4} align="flex-start" width="100%">
      <FormControl isRequired>
        <FormLabel>Service Type</FormLabel>
        <Select 
          value={service.service_type}
          onChange={(e) => onUpdate({ ...service, service_type: e.target.value })}
        >
          <option value="">Select One</option>
          <option value="OT">Occupational Therapy</option>
          <option value="ST">Speech Therapy</option>
          <option value="PT">Physical Therapy</option>
          <option value="PG">Play Group</option>
          <option value="FT">Feeding Therapy</option>
        </Select>
      </FormControl>

      <FormControl>
        <FormLabel>Frequency</FormLabel>
        <Input
          value={service.frequency}
          onChange={(e) => onUpdate({ ...service, frequency: e.target.value })}
          placeholder="e.g., 2x/week"
        />
      </FormControl>

      <FormControl>
        <FormLabel>Base Rate</FormLabel>
        <Input
            type="number"
            min={0}
            value={service.base_rate}
            onChange={(e) => onUpdate({ ...service, base_rate: e.target.value })}
        />
      </FormControl>

      <FormControl>
        <FormLabel>Reservation Fee</FormLabel>
        <NumberInput
            min={0}
            value={service.reservation_fee || ''}
            onChange={(valueString) => onUpdate({ ...service, reservation_fee: valueString })}
        >
            <NumberInputField placeholder="Optional" />
        </NumberInput>
      </FormControl>

      <FormControl display="flex" alignItems="center" mt={8}>
        <FormLabel mb="0">VAT Inclusive</FormLabel>
        <Switch
          isChecked={service.vat_inclusive}
          onChange={(e) => onUpdate({ ...service, vat_inclusive: e.target.checked })}
        />
      </FormControl>

      <IconButton
        icon={<DeleteIcon />}
        colorScheme="red"
        onClick={() => onDelete(service)}
        aria-label="Delete service"
        mt={8}
      />
    </HStack>

    <HStack spacing={4} align="flex-end" width="100%">
      <FormControl>
        <FormLabel>Sunday Rate</FormLabel>
        <Input
          type="number"
          min={0}
          value={service.sunday_rate ?? ''}
          onChange={(e) => onUpdate({ ...service, sunday_rate: e.target.value })}
          placeholder="Optional premium rate"
          isDisabled={service.sunday_regular_rate}
        />
      </FormControl>

      <FormControl display="flex" alignItems="center" pb={2}>
        <Checkbox
          isChecked={Boolean(service.sunday_regular_rate)}
          onChange={(e) => onUpdate({
            ...service,
            sunday_regular_rate: e.target.checked,
          })}
        >
          Regular rate on Sundays
        </Checkbox>
      </FormControl>
      <Text fontSize="xs" color="gray.500" pb={2}>
        Check this for clients who keep weekday pricing on Sunday sessions.
      </Text>
    </HStack>
    </VStack>
  );
};

const ClientForm = ({ isOpen, onClose, initialData = null, onSave }) => {
  const [formData, setFormData] = useState({
    name: '',
    nickname: '', 
    age: '',
    birthdate: '',
    gender: '',
    diagnosis: '',
    pwd: false,
    pwd_number: '',
    guardian_name: '',
    guardian_contact: '',
    guardian_email: '',
    status: 'Active',
    notes: '',
    services: [],
  });
  const [allTherapists, setAllTherapists] = useState([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const toast = useToast();

  useEffect(() => {
    if (initialData) {
      // Filter only currently valid services
      const currentDate = getLocalDateString(new Date());
      const validServices = initialData.client_services?.filter((service) =>
        isPricingActiveOnDate(service, currentDate)
      ) || [];

      const resolvedAge =
        initialData.birthdate != null && initialData.birthdate !== ''
          ? calculateAgeFromBirthdate(initialData.birthdate)
          : null;

      setFormData({
        name: initialData.name || '',
        nickname: initialData.nickname || '',
        age: resolvedAge !== null ? String(resolvedAge) : initialData.age || '',
        birthdate: initialData.birthdate || '',
        gender: initialData.gender || '',
        diagnosis: initialData.diagnosis || '',
        pwd: Boolean(initialData.pwd),
        pwd_number: initialData.pwd_number || '',
        guardian_name: initialData.guardian_name || '',
        guardian_contact: initialData.guardian_contact || '',
        guardian_email: initialData.guardian_email || '',
        status: initialData.status || 'Active',
        notes: initialData.notes || '',
        services: validServices.map(service => ({
            id: service.id,
            service_type: service.service_type || '',
            frequency: service.frequency || '',
            base_rate: service.base_rate || '',
            sunday_rate: service.sunday_rate ?? '',
            sunday_regular_rate: Boolean(service.sunday_regular_rate),
            reservation_fee: service.reservation_fee ? String(service.reservation_fee) : '',
            vat_inclusive: Boolean(service.vat_inclusive)
        }))
      });
    } else {
      // Reset form to initial state when initialData is null
      setFormData({
        name: '',
        nickname: '',
        age: '',
        birthdate: '',
        gender: '',
        diagnosis: '',
        pwd: false,
        pwd_number: '',
        guardian_name: '',
        guardian_contact: '',
        guardian_email: '',
        status: 'Active',
        notes: '',
        services: []
      });
    }
  }, [initialData]);

  const calculateAge = (birthdate) => {
    const n = calculateAgeFromBirthdate(birthdate);
    return n === null ? '' : String(n);
  };
  
  const handleBirthdateChange = (e) => {
    const birthdate = e.target.value;
    const calculatedAge = calculateAge(birthdate);
    
    setFormData(prev => ({
      ...prev,
      birthdate,
      age: calculatedAge
    }));
  };

  const fetchTherapists = async () => {
    const { data, error } = await supabase
      .from('therapists')
      .select('*')
      .eq('status', 'active');
    
    if (!error) {
      setAllTherapists(data);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
  
    try {
      // First, save/update the client
      const computedAge =
        formData.birthdate ? calculateAgeFromBirthdate(formData.birthdate) : null;

      const clientData = {
        name: formData.name,
        nickname: formData.nickname,
        age: computedAge !== null ? computedAge : formData.age,
        gender: formData.gender,
        diagnosis: formData.diagnosis,
        pwd: formData.pwd,
        pwd_number: formData.pwd ? formData.pwd_number : null,
        guardian_name: formData.guardian_name,
        guardian_contact: formData.guardian_contact,
        guardian_email: formData.guardian_email || null,
        birthdate: formData.birthdate || null,
        status: formData.status,
        notes: formData.notes
      };
  
      let clientId;
      if (initialData) {
        console.log('Status: ', clientData.status);
        const { error } = await supabase
          .from('clients')
          .update(clientData)
          .eq('id', initialData.id);
        if (error) throw error;
        clientId = initialData.id;

        const currentDate = getLocalDateString(new Date());
        console.log('Date: ', currentDate);

        if (clientData.status === 'Inactive') {
          console.log('ENTERED');
          const { error } = await supabase
            .from('sessions')
            .update({ valid_until: currentDate })
            .eq('client_id', initialData.id)
            .is('valid_until', null);  // Only update rows where valid_until is null
          if (error) throw error;
        }
      
        // Get current valid services
        const { data: existingServices, error: servicesError } = await supabase
          .from('client_services')
          .select('*')
          .eq('client_id', clientId)
          .is('pricing_valid_until', null);
  
        if (servicesError) throw servicesError;
  
        // Update existing services that have changed
        for (const existingService of existingServices) {
          const updatedService = formData.services.find(s => 
            s.service_type === existingService.service_type
          );
  
          if (updatedService && (
            updatedService.base_rate !== existingService.base_rate ||
            updatedService.sunday_rate !== existingService.sunday_rate ||
            updatedService.sunday_regular_rate !== existingService.sunday_regular_rate ||
            updatedService.vat_inclusive !== existingService.vat_inclusive
          )) {

            // Calculate previous day for valid_until (local calendar date)
            const previousDay = parseLocalDateString(currentDate);
            previousDay.setDate(previousDay.getDate() - 1);
            const previousDayStr = getLocalDateString(previousDay);

            // Set end date for old service
            await supabase
              .from('client_services')
              .update({ pricing_valid_until: previousDayStr })
              .eq('id', existingService.id);
  
            // Insert new service record
            await supabase
              .from('client_services')
              .insert({
                client_id: clientId,
                service_type: updatedService.service_type,
                frequency: updatedService.frequency,
                base_rate: updatedService.base_rate,
                sunday_rate: updatedService.sunday_rate || null,
                sunday_regular_rate: Boolean(updatedService.sunday_regular_rate),
                reservation_fee: updatedService.reservation_fee || null,
                vat_inclusive: updatedService.vat_inclusive,
                pricing_valid_from: previousDayStr
              });
          }
        }
  
        // Insert new services
        const newServices = formData.services.filter(service => 
          !existingServices.some(es => es.service_type === service.service_type)
        );
  
        if (newServices.length > 0) {
          const newServicesData = newServices.map(service => ({
            client_id: clientId,
            service_type: service.service_type,
            frequency: service.frequency,
            base_rate: service.base_rate,
            sunday_rate: service.sunday_rate || null,
            sunday_regular_rate: Boolean(service.sunday_regular_rate),
            reservation_fee: service.reservation_fee || null,
            vat_inclusive: service.vat_inclusive,
            pricing_valid_from: currentDate
          }));
  
          const { error: newServicesError } = await supabase
            .from('client_services')
            .insert(newServicesData);
  
          if (newServicesError) throw newServicesError;
        }
      } else {
        // Handle new client creation
        const { data, error } = await supabase
          .from('clients')
          .insert([clientData])
          .select()
          .single();
        if (error) throw error;
        clientId = data.id;
  
        // Insert all services for new client
        if (formData.services.length > 0) {
          const currentDate = getLocalDateString(new Date());
          const servicesData = formData.services.map(service => ({
            client_id: clientId,
            service_type: service.service_type,
            frequency: service.frequency,
            base_rate: service.base_rate,
            sunday_rate: service.sunday_rate || null,
            sunday_regular_rate: Boolean(service.sunday_regular_rate),
            reservation_fee: service.reservation_fee || null,
            vat_inclusive: service.vat_inclusive,
            pricing_valid_from: currentDate
          }));
  
          const { error: servicesError } = await supabase
            .from('client_services')
            .insert(servicesData);
  
          if (servicesError) throw servicesError;
        }
      }
  
      toast({
        title: initialData ? 'Client Updated' : 'Client Added',
        status: 'success',
        duration: 5000,
        isClosable: true,
      });
  
      onSave();
      onClose();
    } catch (error) {
      toast({
        title: 'Error',
        description: error.message,
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleAddService = () => {
    setFormData(prev => ({
      ...prev,
      services: [...prev.services, {
        service_type: '',
        frequency: '',
        base_rate: '',
        sunday_rate: '',
        sunday_regular_rate: false,
        therapists: []
      }]
    }));
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="4xl">
      <ModalOverlay />
      <ModalContent>
        <form onSubmit={handleSubmit}>
          <ModalHeader>
            {initialData ? 'Edit Client' : 'Add New Client'}
          </ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <VStack spacing={6}>
              {/* Basic Information */}
              <Box width="100%">
                <Heading size="sm" mb={4}>Basic Information</Heading>
                <VStack spacing={4} align="stretch">
                  <HStack spacing={4}>
                    <FormControl isRequired>
                        <FormLabel>Child's Name</FormLabel>
                        <Input
                        value={formData.name}
                        onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                        />
                    </FormControl>

                    <FormControl>
                        <FormLabel>Nickname</FormLabel>
                        <Input
                            value={formData.nickname}
                            onChange={(e) => setFormData(prev => ({ ...prev, nickname: e.target.value }))}
                            placeholder="Optional"
                        />
                    </FormControl>
                  </HStack>

                  <HStack spacing={4}>
                    <FormControl>
                        <FormLabel>Age</FormLabel>
                        <Input
                            type="number"
                            value={formData.age}
                            onChange={(e) => setFormData(prev => ({ ...prev, age: e.target.value }))}
                            isReadOnly={formData.birthdate} // Make read-only if birthdate is set
                            placeholder={formData.birthdate ? 'Auto-calculated' : 'Enter age'}
                        />
                    </FormControl>

                    <FormControl>
                        <FormLabel>Birthdate</FormLabel>
                        <Input
                            type="date"
                            value={formData.birthdate}
                            onChange={handleBirthdateChange}
                        />
                    </FormControl>

                    <FormControl>
                      <FormLabel>Gender</FormLabel>
                      <Select
                        value={formData.gender}
                        onChange={(e) => setFormData(prev => ({ ...prev, gender: e.target.value }))}
                      >
                        <option value="">Select Gender</option>
                        <option value="Male">Male</option>
                        <option value="Female">Female</option>
                      </Select>
                    </FormControl>
                  </HStack>

                  <FormControl>
                    <FormLabel>Diagnosis</FormLabel>
                    <Input
                      value={formData.diagnosis}
                      onChange={(e) => setFormData(prev => ({ ...prev, diagnosis: e.target.value }))}
                    />
                  </FormControl>

                  <FormControl display="flex" alignItems="center">
                    <HStack spacing={4} width="100%">
                        <Box flex="1">
                        <FormLabel mb="0">PWD</FormLabel>
                        <Switch
                            isChecked={formData.pwd}
                            onChange={(e) => setFormData(prev => ({ ...prev, pwd: e.target.checked }))}
                        />
                        </Box>
                        {formData.pwd && (
                        <FormControl flex="2">
                            <FormLabel>PWD No.</FormLabel>
                            <Input
                            type="text"
                            value={formData.pwd_number || ''}
                            onChange={(e) => setFormData(prev => ({ ...prev, pwd_number: e.target.value }))}
                            placeholder="Enter PWD ID Number"
                            />
                        </FormControl>
                        )}
                    </HStack>
                  </FormControl>
                </VStack>
              </Box>

              <Divider />

              {/* Services */}
              <Box width="100%">
                <Heading size="sm" mb={4}>Services</Heading>
                <VStack spacing={4} align="stretch">
                  <HStack justify="space-between">
                    <Text fontSize="sm" color="gray.600">
                      Configure the therapy services for this client
                    </Text>
                    <Button
                      leftIcon={<AddIcon />}
                      size="sm"
                      onClick={handleAddService}
                    >
                      Add Service
                    </Button>
                  </HStack>

                  {formData.services.map((service, index) => (
                    <ServiceRow
                      key={index}
                      service={service}
                      onDelete={() => {
                        setFormData(prev => ({
                          ...prev,
                          services: prev.services.filter((_, i) => i !== index)
                        }));
                      }}
                      onUpdate={(updatedService) => {
                        setFormData(prev => ({
                          ...prev,
                          services: prev.services.map((s, i) => 
                            i === index ? updatedService : s
                          )
                        }));
                      }}
                      therapists={service.therapists}
                      allTherapists={allTherapists}
                    />
                  ))}
                </VStack>
              </Box>

              <Divider />

              {/* Guardian Information */}
              <Box width="100%">
                <Heading size="sm" mb={4}>Guardian Information</Heading>
                <VStack spacing={4} align="stretch">
                  <FormControl>
                    <FormLabel>Guardian's Name</FormLabel>
                    <Input
                      value={formData.guardian_name}
                      onChange={(e) => setFormData(prev => ({ ...prev, guardian_name: e.target.value }))}
                    />
                  </FormControl>

                  <HStack spacing={4}>
                    <FormControl>
                      <FormLabel>Guardian's Contact Number</FormLabel>
                      <Input
                        value={formData.guardian_contact}
                        onChange={(e) => setFormData(prev => ({ ...prev, guardian_contact: e.target.value }))}
                      />
                    </FormControl>

                    <FormControl>
                      <FormLabel>Guardian's Email (Optional)</FormLabel>
                      <Input
                        type="email"
                        value={formData.guardian_email}
                        onChange={(e) => setFormData(prev => ({ ...prev, guardian_email: e.target.value }))}
                        placeholder="Guardian's email address"
                      />
                    </FormControl>
                  </HStack>
                </VStack>
              </Box>

              <Divider />

              {/* Additional Information */}
              <Box width="100%">
                <Heading size="sm" mb={4}>Additional Information</Heading>
                <VStack spacing={4} align="stretch">
                  <FormControl>
                    <FormLabel>Notes</FormLabel>
                    <Textarea
                      value={formData.notes}
                      onChange={(e) => setFormData(prev => ({ ...prev, notes: e.target.value }))}
                      placeholder="Any additional notes about the client..."
                      rows={3}
                    />
                  </FormControl>

                  <FormControl>
                    <FormLabel>Status</FormLabel>
                    <Select
                      value={formData.status}
                      onChange={(e) => setFormData(prev => ({ ...prev, status: e.target.value }))}
                    >
                      <option value="Active">Active</option>
                      <option value="Inactive">Inactive</option>
                    </Select>
                  </FormControl>
                </VStack>
              </Box>

              {/* Document Management Note for new clients */}
              {!initialData && (
                <Alert status="info">
                  <AlertIcon />
                  <VStack align="start" spacing={1}>
                    <Text fontSize="sm" fontWeight="medium">Document Management</Text>
                    <Text fontSize="sm">
                      Document upload will be available after saving the client. You can then access it through the client details view.
                    </Text>
                  </VStack>
                </Alert>
              )}
            </VStack>
          </ModalBody>

          <ModalFooter>
            <Button type="submit" colorScheme="blue" mr={3} isLoading={isSubmitting}>
              {initialData ? 'Update Client' : 'Add Client'}
            </Button>
            <Button variant="ghost" onClick={onClose}>Cancel</Button>
          </ModalFooter>
        </form>
      </ModalContent>
    </Modal>
  );
};

const ClientAttendanceLog = ({ client, onBack }) => {
  const [attendanceLogs, setAttendanceLogs] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const toast = useToast();

  useEffect(() => {
    fetchAttendanceLogs();
  }, [client]);

  const formatTime = (time) => {
    if (!time) return '';
    const hour = parseInt(time.split(':')[0]);
    const h = hour % 12 || 12;
    const ampm = hour < 12 ? 'AM' : 'PM';
    return `${h}:00 ${ampm}`;
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-PH', {
      style: 'currency',
      currency: 'PHP'
    }).format(amount);
  };

  const fetchAttendanceLogs = async () => {
    try {
      // First, get the attendance logs
      const { data: attendanceData, error: attendanceError } = await supabase
        .from('session_attendance')
        .select(`
          *,
          session:sessions (
            day,
            time,
            is_makeup,
            makeup_for,
            therapist:therapists (
              name,
              specialization
            )
          )
        `)
        .eq('client_id', client.id)
        .order('attendance_date', { ascending: false });

      if (attendanceError) throw attendanceError;

      // Then, get makeup sessions linked to cancelled/late-cancelled rows (completed makeup or scheduled slot)
      const linkedMakeupAttendanceIds = attendanceData
        .filter(
          (log) =>
            isCancelledOrLateCancellationStatus(log.status) &&
            (log.madeup_for === true || log.makeup_scheduled === true)
        )
        .map((log) => log.id);

      let makeupSessionsData = [];
      if (linkedMakeupAttendanceIds.length > 0) {
        console.log('Looking for makeup sessions for attendance IDs:', linkedMakeupAttendanceIds);

        const { data: makeupData, error: makeupError } = await supabase
          .from('sessions')
          .select('session_attendance_id, valid_from, id')
          .in('session_attendance_id', linkedMakeupAttendanceIds);

        if (makeupError) throw makeupError;
        makeupSessionsData = makeupData || [];
        console.log('Found makeup sessions:', makeupSessionsData);
      }

      // Combine the data
      const logsWithMakeupDates = attendanceData.map(log => {
        const makeupSession = makeupSessionsData.find(
          makeup => String(makeup.session_attendance_id) === String(log.id)
        );
        
        if (log.madeup_for === true && isCancelledOrLateCancellationStatus(log.status)) {
          console.log(`For cancelled or late-cancelled session ${log.id} (${log.attendance_date}):`, {
            foundMakeupSession: !!makeupSession,
            makeupDate: makeupSession?.valid_from,
            originalDate: log.attendance_date
          });
        }
        
        return {
          ...log,
          makeup_session_date: makeupSession?.valid_from
        };
      });

      setAttendanceLogs(logsWithMakeupDates);
    } catch (error) {
      toast({
        title: 'Error fetching attendance logs',
        description: error.message,
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setIsLoading(false);
    }
  };

  const getStats = () => {
    const total = attendanceLogs.length;
    const present = attendanceLogs.filter(log => log.status === 'present').length;
    const cancelled = attendanceLogs.filter(log => log.status === 'cancelled').length;
    
    // Count sessions that require makeup (madeup_for is not null - either true or false)
    const requiredForMakeup = attendanceLogs.filter(log => log.madeup_for !== null).length;
    
    // Count sessions that have been made up (madeup_for is true)
    const madeUp = attendanceLogs.filter(log => log.madeup_for === true).length;
    
    // Count makeup sessions (sessions created to make up for others)
    const makeupSessions = attendanceLogs.filter(log => log.session?.is_makeup === true).length;
    
    // Actual sessions = total minus makeup sessions
    const actualSessions = total - makeupSessions;
    
    return { total, present, cancelled, requiredForMakeup, madeUp, actualSessions };
  };

  const stats = getStats();

  if (isLoading) return <Spinner />;

  return (
    <Box>
      <Button leftIcon={<ChevronLeftIcon />} onClick={onBack} mb={6}>
        Back to Clients
      </Button>
      
      <VStack spacing={6} align="stretch">
        <Box>
          <Heading size="lg">{formatNameLastCommaFirst(client.name)}'s Attendance Log</Heading>
          {client.nickname && (
            <Text fontSize="lg" color="gray.600">"{client.nickname}"</Text>
          )}
        </Box>

        <HStack spacing={8} p={4} bg="gray.50" borderRadius="md">
          <Stat>
            <HStack spacing={1}>
              <StatLabel>Total Sessions</StatLabel>
              <Tooltip 
                label="Total number of sessions including makeup sessions. Actual sessions are original scheduled sessions only."
                fontSize="sm"
              >
                <InfoIcon boxSize={3} color="gray.400" />
              </Tooltip>
            </HStack>
            <StatNumber>
              <HStack>
                <Text fontSize="2xl">{stats.total}</Text>
                <Text as="span" fontSize="xs" color="black" fontWeight="semibold" mt={0}>
                  ({stats.actualSessions} actual sessions)
                </Text>
              </HStack>
            </StatNumber>
          </Stat>
          <Stat>
            <StatLabel>Present</StatLabel>
            <StatNumber color="green.500">{stats.present}</StatNumber>
          </Stat>
          <Stat>
            <HStack spacing={1}>
              <StatLabel>Cancelled</StatLabel>
              <Tooltip 
                label="Number in parentheses shows how many cancelled sessions require makeup sessions to be scheduled."
                fontSize="sm"
              >
                <InfoIcon boxSize={3} color="gray.400" />
              </Tooltip>
            </HStack>
            <StatNumber color="orange.500">
              <HStack>
                <Text fontSize="2xl" color="orange.500">{stats.cancelled}</Text>
                <Text as="span" fontSize="xs" color="black" fontWeight="semibold" mt={0}>
                  ({stats.requiredForMakeup} required for makeup)
                </Text>
              </HStack>
            </StatNumber>
          </Stat>
          <Stat>
            <HStack spacing={1}>
              <StatLabel>Made up for</StatLabel>
              <Tooltip 
                label="Shows how many required makeup sessions have been successfully completed."
                fontSize="sm"
              >
                <InfoIcon boxSize={3} color="gray.400" />
              </Tooltip>
            </HStack>
            <StatNumber color="purple.500">
              {stats.madeUp} out of {stats.requiredForMakeup}
            </StatNumber>
          </Stat>
        </HStack>

        <Table variant="simple">
          <Thead>
            <Tr>
              <Th>Date/Time</Th>
              <Th>Session Type</Th>
              <Th>Service Type</Th>
              <Th>Therapist</Th>
              <Th>Status</Th>
              <Th>Amount Paid</Th>
              <Th>Notes</Th>
            </Tr>
          </Thead>
          <Tbody>
            {attendanceLogs.map(log => (
              <Tr key={log.id}>
                <Td>
                  <VStack align="start" spacing={0}>
                  <Text>{new Date(log.attendance_date).toLocaleDateString('en-US', {
                    year: 'numeric',
                    month: 'long',
                    day: 'numeric'
                  })}</Text>
                    <Text fontSize="sm" color="gray.600">
                      {log.session.day}, {formatTime(log.session.time)}
                    </Text>
                  </VStack>
                </Td>
                <Td>
                  <Badge colorScheme={log.session_type === 'Regular' ? 'blue' : 'green'}>
                    {log.session_type}
                  </Badge>
                </Td>
                <Td>{log.session.therapist.specialization}</Td>
                <Td>{log.session.therapist.name}</Td>
                <Td>
                  <VStack align="start" spacing={1}>
                    <Badge
                      colorScheme={getAttendanceStatusColorScheme(log.status)}
                    >
                      {formatAttendanceStatusLabel(log.status)}
                    </Badge>
                    {log.session?.is_makeup && log.session?.makeup_for && (
                      <Badge colorScheme="cyan" variant="outline" size="sm">
                        {log.status === 'present'
                          ? `Makeup for ${formatMakeupBadgeDate(log.session.makeup_for)}`
                          : `Supposed Makeup for ${formatMakeupBadgeDate(log.session.makeup_for)}`}
                      </Badge>
                    )}
                    {log.voided_linked_makeup_on &&
                      isCancelledOrLateCancellationStatus(log.status) && (
                        <>
                          <Badge colorScheme="purple" variant="outline" size="sm">
                            Supposed Makeup on{' '}
                            {formatMakeupBadgeDate(log.voided_linked_makeup_on)} cancelled by therapist
                          </Badge>
                          <Badge colorScheme="green" variant="outline" size="sm">
                            Makeup no longer required
                          </Badge>
                        </>
                      )}
                    {log.makeup_fee_voided_by_therapist &&
                      log.session?.is_makeup &&
                      isCancelledOrLateCancellationStatus(log.status) &&
                      log.session?.makeup_for && (
                        <Badge colorScheme="purple" variant="outline" size="sm">
                          Makeup for {formatMakeupBadgeDate(log.session.makeup_for)} cancelled by therapist
                        </Badge>
                      )}
                    {isCancelledOrLateCancellationStatus(log.status) &&
                      log.makeup_scheduled === true &&
                      !log.voided_linked_makeup_on &&
                      log.madeup_for !== true &&
                      log.makeup_session_date && (
                        <Badge colorScheme="blue" variant="outline" size="sm">
                          Makeup scheduled on{' '}
                          {new Date(log.makeup_session_date).toLocaleDateString('en-US', {
                            month: 'short',
                            day: 'numeric',
                            year: 'numeric'
                          })}
                        </Badge>
                      )}
                    {log.paid_noshow && (
                      <Badge colorScheme="orange" variant="solid" size="sm">
                        Paid No Show Fee
                      </Badge>
                    )}
                    {log.grace_cancellation && log.status === 'absent' && (
                      <Badge colorScheme="teal" variant="solid" size="sm">
                        Grace Cancellation
                      </Badge>
                    )}
                    {log.waived_late_cancellation && log.status === 'absent' && (
                      <Badge colorScheme="cyan" variant="solid" size="sm">
                        Waived Late Cancellation
                      </Badge>
                    )}
                    {log.madeup_for === false && log.makeup_scheduled === false && !log.paid_noshow && (
                      <Badge colorScheme="red" variant="outline" size="sm">
                        Makeup Required
                      </Badge>
                    )}
                    {log.madeup_for === true &&
                      isCancelledOrLateCancellationStatus(log.status) &&
                      log.makeup_session_date && (
                      <Badge colorScheme="green" variant="outline" size="sm">
                        Made up for on {new Date(log.makeup_session_date).toLocaleDateString('en-US', {
                          month: 'short',
                          day: 'numeric',
                          year: 'numeric'
                        })}
                      </Badge>
                    )}
                  </VStack>
                </Td>
                <Td>{log.amount_paid ? formatCurrency(log.amount_paid) : '-'}</Td>
                <Td>{log.notes || '-'}</Td>
              </Tr>
            ))}
          </Tbody>
        </Table>
      </VStack>
    </Box>
  );
};

// Continuing from where we left off...

const ClientManagement = ({ LoadingComponent }) => {
    const [clients, setClients] = useState([]);
    const [filteredClients, setFilteredClients] = useState([]);
    const [selectedClient, setSelectedClient] = useState(null);
    const [isLoading, setIsLoading] = useState(true);
    const { isOpen, onOpen, onClose } = useDisclosure();
    const [selectedClientForDetail, setSelectedClientForDetail] = useState(null);
    const { isOpen: isDetailOpen, onOpen: onDetailOpen, onClose: onDetailClose } = useDisclosure();
    const toast = useToast();

    // Search and filter states
    const [searchTerm, setSearchTerm] = useState('');
    const [therapistFilter, setTherapistFilter] = useState('');
    const [serviceFilter, setServiceFilter] = useState('');
    const [showActiveOnly, setShowActiveOnly] = useState(false);
    const [clientViewMode, setClientViewMode] = useState('list');
    const [attendanceBounds, setAttendanceBounds] = useState({});

    // Unique therapists and services for dropdown
    const [uniqueTherapists, setUniqueTherapists] = useState([]);
    const [uniqueServices, setUniqueServices] = useState([]);

    const [selectedClientForLog, setSelectedClientForLog] = useState(null);
  
    useEffect(() => {
      fetchClients();
    }, []);

    // Update filtered clients whenever search or filters change
    useEffect(() => {
        let result = clients;

        // Name search filter
        if (searchTerm) {
            result = result.filter(client => 
                client.name.toLowerCase().includes(searchTerm.toLowerCase())
            );
        }

        // Therapist filter
        if (therapistFilter) {
            result = result.filter(client => 
                client.services.some(service => 
                    service.therapists.includes(therapistFilter)
                )
            );
        }

        // Service filter
        if (serviceFilter) {
            result = result.filter(client => 
                client.services.some(service => 
                    service.service_type === serviceFilter
                )
            );
        }

        if (showActiveOnly) {
            result = result.filter(client => client.status === 'Active');
        }

        setFilteredClients(result);
    }, [clients, searchTerm, therapistFilter, serviceFilter, showActiveOnly]);

    // Calculate detailed service statistics
    const calculateServiceStats = () => {
        const activeClients = filteredClients.filter(client => client.status === 'Active');
        const inactiveClients = filteredClients.filter(client => client.status === 'Inactive');
        
        const stats = {
            total: filteredClients.length,
            active: activeClients.length,
            activePwd: activeClients.filter(client => client.pwd).length,
            inactive: inactiveClients.length,
            inactivePwd: inactiveClients.filter(client => client.pwd).length,
            services: {
                OT: { total: 0, pwd: 0 },
                ST: { total: 0, pwd: 0 },
                PT: { total: 0, pwd: 0 },
                PG: { total: 0, pwd: 0 }
            }
        };

        activeClients.forEach(client => {
            // Get current valid services for this client
            const currentDate = getLocalDateString(new Date());
            const validServices = client.client_services?.filter((service) =>
                isPricingActiveOnDate(service, currentDate)
            ) || [];

            // Count unique service types for this client
            const clientServiceTypes = [...new Set(validServices.map(service => service.service_type))];
            
            clientServiceTypes.forEach(serviceType => {
                if (stats.services[serviceType]) {
                    stats.services[serviceType].total++;
                    if (client.pwd) {
                        stats.services[serviceType].pwd++;
                    }
                }
            });
        });

        return stats;
    };
  
    const fetchClients = async () => {
        try {
            // Fetch clients with their services
            const { data: clients, error: clientsError } = await supabase
            .from('clients')
            .select(`
                *,
                client_services (
                    *
                ),
                sessions (
                    id,
                    client_id,
                    therapist_id,
                    session_type,
                    day,
                    time,
                    valid_from,
                    valid_until,
                    is_makeup,
                    therapist:therapists (
                        id,
                        name,
                        specialization
                    )
                )
            `);
    
            if (clientsError) throw clientsError;

            const { data: attendanceRows, error: attendanceError } = await supabase
              .from('session_attendance')
              .select('client_id, attendance_date')
              .eq('status', 'present');

            if (attendanceError) throw attendanceError;

            const boundsByClient = {};
            (attendanceRows || []).forEach((row) => {
              if (!boundsByClient[row.client_id]) {
                boundsByClient[row.client_id] = {
                  firstPresentDate: row.attendance_date,
                  lastPresentDate: row.attendance_date,
                  presentDates: [row.attendance_date],
                };
                return;
              }

              boundsByClient[row.client_id].presentDates.push(row.attendance_date);

              if (row.attendance_date < boundsByClient[row.client_id].firstPresentDate) {
                boundsByClient[row.client_id].firstPresentDate = row.attendance_date;
              }
              if (row.attendance_date > boundsByClient[row.client_id].lastPresentDate) {
                boundsByClient[row.client_id].lastPresentDate = row.attendance_date;
              }
            });
            setAttendanceBounds(boundsByClient);
    
            const currentDate = getLocalDateString(new Date());
            // Process the data to combine services and sessions
            const processedClients = clients.map(client => {
                // Filter valid services first
                const validServices = Array.isArray(client.client_services)
                    ? client.client_services.filter((service) =>
                        isPricingActiveOnDate(service, currentDate)
                      )
                    : [];
    
                return {
                    ...client,
                    // Preserve full session history for longevity view (list UI filters separately)
                    sessions: client.sessions || [],
                    client_services: client.client_services || [],
                    services: validServices.map(service => {
                        const specializationMap = {
                            'OT': 'Occupational Therapy',
                            'ST': 'Speech Therapy',
                            'PT': 'Physical Therapy',
                            'PG': 'Play Group',
                            'FT': 'Feeding Therapy'
                        };

                        // Permanent recurring schedule only (open-ended, not makeup/one-day)
                        const validSessions = (client.sessions || [])
                            .filter(session => 
                                session.client_id === client.id && 
                                session.therapist?.specialization === specializationMap[service.service_type] &&
                                isPermanentClientSession(session)
                            );
    

                        const serviceSessions = validSessions
                            .map(session => ({
                                day: session.day,
                                time: formatTime(session.time)
                            }));

                        const serviceTherapists = validSessions
                            .map(session => session.therapist.name)
                            .filter((value, index, self) => self.indexOf(value) === index);
    
                        return {
                            id: service.id,
                            service_type: service.service_type,
                            frequency: service.frequency,
                            base_rate: service.base_rate,
                            therapists: serviceTherapists,
                            schedule: serviceSessions.length > 0 
                                ? serviceSessions.map(s => `${s.day} ${s.time}`).join(' | ')
                                : 'Not scheduled'
                        };
                    })
                };
            });

            // Add the sorting before setting the state:
            const sortedClients = processedClients.sort(compareNamesByFamilyName);
            setClients(processedClients);

            // Extract unique therapists and services
            const allTherapists = new Set();
            const allServices = new Set();

            processedClients.forEach(client => {
                client.services.forEach(service => {
                    service.therapists.forEach(therapist => allTherapists.add(therapist));
                    allServices.add(service.service_type);
                });
            });

            setUniqueTherapists(Array.from(allTherapists));
            setUniqueServices(Array.from(allServices));
        } catch (error) {
            console.error('Error details:', error);
            toast({
                title: 'Error fetching clients',
                description: error.message,
                status: 'error',
                duration: 5000,
                isClosable: true,
            });
        } finally {
            setIsLoading(false);
        }
    };

    // Add these helper functions here
    // Helper function to format time
    const formatTime = (time) => {
        if (!time) return '';
        const hour = parseInt(time.split(':')[0]);
        const h = hour % 12 || 12;
        const ampm = hour < 12 ? 'AM' : 'PM';
        return `${h}:00 ${ampm}`;
    };

    const handleViewClient = (client) => {
      setSelectedClientForDetail(client);
      onDetailOpen();
    };
  
    const handleEditClient = (client) => {
        console.log('Original client data:', client); // For debugging
        console.log('Client Age:', client.age); // For debugging
        const formattedClient = {
          ...client,
          age: client.age ? String(client.age) : '',
          birthdate: client.birthdate || '',
          gender: client.gender || '',
          diagnosis: client.diagnosis || '',
          pwd: Boolean(client.pwd),
          pwd_number: client.pwd_number || '',
          guardian_name: client.guardian_name || '',
          guardian_contact: client.guardian_contact || '',
          guardian_email: client.guardian_email || '', // Add this line
          status: client.status || 'Active',
          notes: client.notes || '',
          services: (client.client_services || []).map(service => ({
              id: service.id,
              service_type: service.service_type,
              frequency: service.frequency,
              base_rate: service.base_rate ? String(service.base_rate) : '',
              reservation_fee: service.reservation_fee ? String(service.reservation_fee) : ''
          }))
        };
        console.log('Formatted client data:', formattedClient); // For debugging
        setSelectedClient(formattedClient);
        onOpen();
    };
  
    const handleCloseForm = () => {
      setSelectedClient(null);
      onClose();
    };
  
    const handleClientSaved = () => {
      fetchClients();
      handleCloseForm();
    };

    if (isLoading) {
        return <LoadingComponent />;
    }
  
    const serviceStats = calculateServiceStats();
  
    return (
      <Box>
        {!selectedClientForLog &&
        <VStack spacing={4} mb={4}>
            <HStack width="100%" flexWrap="wrap" spacing={4} align="center">
                <Input 
                    placeholder="Search by client name" 
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    maxW={{ base: '100%', md: '280px' }}
                    flex="1"
                />
                <Select 
                    placeholder="Filter by Therapist"
                    value={therapistFilter}
                    onChange={(e) => setTherapistFilter(e.target.value)}
                    maxW={{ base: '100%', md: '220px' }}
                >
                    {uniqueTherapists
                      .sort((a, b) => a.localeCompare(b))
                      .map(therapist => (
                        <option key={therapist} value={therapist}>
                            {therapist}
                        </option>
                    ))}
                </Select>
                <Select 
                    placeholder="Filter by Service"
                    value={serviceFilter}
                    onChange={(e) => setServiceFilter(e.target.value)}
                    maxW={{ base: '100%', md: '160px' }}
                >
                    {uniqueServices
                      .sort((a, b) => a.localeCompare(b))
                      .map(service => (
                        <option key={service} value={service}>
                            {service}
                        </option>
                    ))}
                </Select>
                <FormControl display="flex" alignItems="center" w="auto">
                  <FormLabel htmlFor="active-only-filter" mb={0} whiteSpace="nowrap" fontSize="sm">
                    Active only
                  </FormLabel>
                  <Switch
                    id="active-only-filter"
                    isChecked={showActiveOnly}
                    onChange={(e) => setShowActiveOnly(e.target.checked)}
                    colorScheme="green"
                  />
                </FormControl>
                <ButtonGroup size="sm" isAttached variant="outline">
                  <Button
                    colorScheme={clientViewMode === 'list' ? 'blue' : 'gray'}
                    variant={clientViewMode === 'list' ? 'solid' : 'outline'}
                    onClick={() => setClientViewMode('list')}
                  >
                    List
                  </Button>
                  <Button
                    colorScheme={clientViewMode === 'longevity' ? 'blue' : 'gray'}
                    variant={clientViewMode === 'longevity' ? 'solid' : 'outline'}
                    onClick={() => setClientViewMode('longevity')}
                  >
                    Longevity
                  </Button>
                </ButtonGroup>
            </HStack>
            {showActiveOnly && (
              <Text fontSize="sm" color="gray.600">
                {clientViewMode === 'list'
                  ? 'Table and summary statistics below include active clients only.'
                  : 'Longevity chart includes active clients only.'}
              </Text>
            )}
            {clientViewMode === 'longevity' && (
              <Text fontSize="sm" color="gray.600">
                Bars show active stints only — gaps appear when a client went inactive and returned.
                Tenure is the sum of active periods (inactive time excluded). Click a row to open the attendance log.
              </Text>
            )}
        </VStack>
        }
        {!selectedClientForLog &&
        <Button 
          colorScheme="blue" 
          onClick={() => {
            setSelectedClient(null); // Explicitly set to null before opening
            onOpen();
          }}
          mb={4}
        >
          Add New Client
        </Button>
        }

        {/* Enhanced Client Statistics */}
        {!selectedClientForLog && (
          <VStack spacing={4} mb={6}>
            {/* Basic Stats Row */}
            <HStack spacing={4} p={4} bg="gray.50" borderRadius="md" width="100%">
              <Stat>
                <StatLabel>Total Clients</StatLabel>
                <StatNumber color="blue.500">{serviceStats.total}</StatNumber>
              </Stat>
                             <Stat>
                 <StatLabel>Active</StatLabel>
                 <StatNumber color="green.500">
                   {serviceStats.active}
                   <Text as="span" fontSize="sm" color="gray.600" ml={2}>
                     ({serviceStats.activePwd} PWD)
                   </Text>
                 </StatNumber>
               </Stat>
               <Stat>
                 <StatLabel>Inactive</StatLabel>
                 <StatNumber color="red.500">
                   {serviceStats.inactive}
                   <Text as="span" fontSize="sm" color="gray.600" ml={2}>
                     ({serviceStats.inactivePwd} PWD)
                   </Text>
                 </StatNumber>
               </Stat>
            </HStack>
            
            {/* Service Stats Row */}
            <HStack spacing={4} p={4} bg="blue.50" borderRadius="md" width="100%">
              <Stat>
                <StatLabel>Occupational Therapy (OT)</StatLabel>
                <StatNumber color="purple.600">
                  {serviceStats.services.OT.total}
                  <Text as="span" fontSize="sm" color="gray.600" ml={2}>
                    ({serviceStats.services.OT.pwd} PWD)
                  </Text>
                </StatNumber>
              </Stat>
              <Stat>
                <StatLabel>Speech Therapy (ST)</StatLabel>
                <StatNumber color="teal.600">
                  {serviceStats.services.ST.total}
                  <Text as="span" fontSize="sm" color="gray.600" ml={2}>
                    ({serviceStats.services.ST.pwd} PWD)
                  </Text>
                </StatNumber>
              </Stat>
              <Stat>
                <StatLabel>Physical Therapy (PT)</StatLabel>
                <StatNumber color="orange.600">
                  {serviceStats.services.PT.total}
                  <Text as="span" fontSize="sm" color="gray.600" ml={2}>
                    ({serviceStats.services.PT.pwd} PWD)
                  </Text>
                </StatNumber>
              </Stat>
            </HStack>
          </VStack>
        )}

        {selectedClientForLog && 
        <ClientAttendanceLog 
            client={selectedClientForLog}
            onBack={() => setSelectedClientForLog(null)}
            />
        }

        {!selectedClientForLog && clientViewMode === 'longevity' && (
          <ClientLongevityView
            clients={filteredClients}
            attendanceBounds={attendanceBounds}
            onSelectClient={setSelectedClientForLog}
          />
        )}

        {!selectedClientForLog && clientViewMode === 'list' &&
        <Table variant="simple">
          <Thead>
            <Tr>
              <Th>Child's Name</Th>
              <Th>Age/Gender</Th>
              <Th>Guardian</Th>
              <Th>Services & Therapists</Th>
              <Th>Status</Th>
              <Th>Actions</Th>
            </Tr>
          </Thead>
          <Tbody>
            {filteredClients.map(client => (
              <Tr 
                key={client.id} 
                onClick={(e) => {
                  // Prevent row click when clicking action buttons
                  if (e.target.closest('button')) return;
                  setSelectedClientForLog(client);
                }}
                cursor="pointer"
                _hover={{ bg: 'gray.50' }}
              >
                <Td>
                    <VStack align="start" spacing={0}>
                        <Text>{formatNameLastCommaFirst(client.name)}</Text>
                        {client.nickname && (
                        <Text fontSize="sm" color="gray.600">"{client.nickname}"</Text>
                        )}
                        <Text fontSize="sm" color="gray.600">
                            {client.pwd ? 'PWD' : 'Non-PWD'}
                        </Text>
                    </VStack>
                </Td>
                <Td>{`${getClientDisplayAge(client) ?? '—'}/${client.gender?.[0] ?? '—'}`}</Td>
                <Td>
                <VStack align="start" spacing={0}>
                    <Text>{client.guardian_name}</Text>
                    <Text fontSize="sm" color="gray.600">
                    {client.guardian_contact}
                    </Text>
                </VStack>
                </Td>
                <Td>
                <VStack align="start" spacing={3}>
                    {client.services
                    .filter((service) => {
                        const currentDate = getLocalDateString(new Date());
                        return isPricingActiveOnDate(service, currentDate);
                    })
                    .map((service, index) => (
                        <Box key={index}>
                        <Text fontWeight="medium">
                            {service.service_type} - {service.frequency}
                        </Text>
                        {service.therapists && service.therapists.length > 0 ? (
                            <Text fontSize="sm" color="gray.600">
                            Therapist/s: {service.therapists.join(', ')}
                            </Text>
                        ) : (
                            <Text fontSize="sm" color="gray.400" fontStyle="italic">
                            No therapists assigned
                            </Text>
                        )}
                        <Text fontSize="sm" color="gray.500">
                            Schedule: {service.schedule ? service.schedule : 'Not specified'}
                        </Text>
                        </Box>
                    ))}
                </VStack>
                </Td>
                <Td>
                <Badge
                    colorScheme={client.status === 'Active' ? 'green' : 'red'}
                >
                    {client.status}
                </Badge>
                </Td>
                <Td>
                  <HStack spacing={2}>
                    <Tooltip label="Edit client information">
                      <IconButton
                        icon={<EditIcon />}
                        onClick={() => handleEditClient(client)}
                        aria-label="Edit client"
                        size="sm"
                        colorScheme="blue"
                        variant="outline"
                      />
                    </Tooltip>
                    <Tooltip label="View client details">
                      <IconButton
                        icon={<ViewIcon />}
                        onClick={() => handleViewClient(client)}
                        aria-label="View client details"
                        size="sm"
                        colorScheme="green"
                        variant="outline"
                      />
                    </Tooltip>
                  </HStack>
                </Td>
              </Tr>
            ))}
          </Tbody>
        </Table>
      }
  
        <ClientForm
          isOpen={isOpen}
          onClose={handleCloseForm}
          initialData={selectedClient}
          onSave={handleClientSaved}
        />
      {/* Add the new ClientDetailModal */}
      <ClientDetailModal
        isOpen={isDetailOpen}
        onClose={onDetailClose}
        client={selectedClientForDetail}
      />
      </Box>
    );
  };
  
  export default ClientManagement;